import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Heart, Sparkles, Award, Clock, MapPin, Scissors, ShoppingBag } from 'lucide-react';

export default function About() {
  return (
    <div data-testid="about-page">
      {/* Hero Section */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-5xl sm:text-6xl font-[Playfair_Display] text-[#2A2626] tracking-tight mb-6"
          >
            About Protect Your Crown
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-lg text-[#5C5855] leading-relaxed max-w-2xl mx-auto"
          >
            Protect Your Crown is a Sacramento-based artisan crochet brand founded in 2023 by Christian Clark. We specialize in handcrafted dreadlock hats, beanie hats, slouchy tams, infinity scarves, and shawls made from premium yarns. Every item is crocheted entirely by hand -- no machines, no shortcuts.
          </motion.p>
        </div>
      </section>
      
      {/* Founder Story */}
      <section className="py-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_protect-your-crown/artifacts/9wiwyrj8_CJhome%20page.jpg"
                alt="Christian Clark crocheting handmade hats in Sacramento studio"
                className="w-full rounded-lg shadow-lg"
              />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <h2 className="text-4xl font-[Playfair_Display] text-[#2A2626] tracking-tight mb-6">
                Founded in 2023 by Christian Clark
              </h2>
              <div className="space-y-4 text-[#5C5855] leading-relaxed">
                <p>
                  Christian Clark is a self-taught crocheter based in Sacramento, California. In 2023, Christian launched Protect Your Crown with a mission to create high-quality, handmade hats that are both comfortable and expressive. The brand's name reflects a core belief: your head deserves to be protected and celebrated with something made by hand, with intention.
                </p>
                <p>
                  Christian specializes in dreadlock-friendly hat designs. Many mass-produced hats don't accommodate locs, braids, or thick natural hair comfortably. Protect Your Crown solves that problem by crocheting hats with extra depth, stretch, and room, so they sit comfortably without pulling, snagging, or flattening hair.
                </p>
                <p>
                  Today, the Protect Your Crown collection includes 15 unique handcrafted designs ranging from $30 beanies to the $100 Aurora Chevron Shawl, a 40+ hour masterpiece. Every piece is made to order or from limited stock, ensuring each customer gets something truly one-of-a-kind.
                </p>
                <p>
                  Christian personally handles every step: selecting yarns, designing original patterns, crocheting each piece by hand, finishing, inspecting, and shipping. This hands-on approach means quality control at every stage.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Quick Facts */}
      <section className="py-16 bg-[#A85A43]" data-testid="quick-facts-section">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center text-white">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="text-4xl font-[Playfair_Display] font-bold mb-2">2023</div>
              <p className="text-white/80 text-sm font-[Manrope]">Year Founded</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <div className="text-4xl font-[Playfair_Display] font-bold mb-2">15+</div>
              <p className="text-white/80 text-sm font-[Manrope]">Unique Designs</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <div className="text-4xl font-[Playfair_Display] font-bold mb-2">40+</div>
              <p className="text-white/80 text-sm font-[Manrope]">Hours Per Shawl</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
            >
              <div className="text-4xl font-[Playfair_Display] font-bold mb-2">100%</div>
              <p className="text-white/80 text-sm font-[Manrope]">Handmade</p>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* What We Make */}
      <section className="py-24 bg-white" data-testid="what-we-make-section">
        <div className="max-w-6xl mx-auto px-6">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl sm:text-5xl font-[Playfair_Display] text-[#2A2626] tracking-tight text-center mb-6"
          >
            What We Make
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-lg text-[#5C5855] text-center max-w-2xl mx-auto mb-16"
          >
            Our product lineup includes hats, scarves, and shawls. Here's a breakdown of what Protect Your Crown offers:
          </motion.p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-[#F9F6F0] rounded-lg p-8 border border-[#EADCCC]"
            >
              <h3 className="text-xl font-[Playfair_Display] text-[#2A2626] mb-3">Beanie Hats</h3>
              <p className="text-sm text-[#5C5855] leading-relaxed mb-4">
                Classic crocheted beanies in solid colors, gradients, and multi-color patterns. Made with soft acrylic yarn, stretchy enough to fit most adult head sizes. Takes 4-6 hours to crochet per hat.
              </p>
              <p className="text-[#A85A43] font-[Manrope] font-semibold">Starting at $30</p>
              <p className="text-xs text-[#5C5855] mt-2">Styles: Soul-shine, Ocean Breeze, Forest Dreams, Sunset Glow, Minty Bong Water, Seasons of Joy</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-[#F9F6F0] rounded-lg p-8 border border-[#EADCCC]"
            >
              <h3 className="text-xl font-[Playfair_Display] text-[#2A2626] mb-3">Dreadlock Hats & Tams</h3>
              <p className="text-sm text-[#5C5855] leading-relaxed mb-4">
                Oversized slouchy hats designed specifically for dreadlocks, braids, twists, and natural hair. Extra depth and stretch prevent snagging. The XL Rasta Dreadlock Tam is our largest dread hat. Takes 6-8 hours to crochet.
              </p>
              <p className="text-[#A85A43] font-[Manrope] font-semibold">$30 - $60</p>
              <p className="text-xs text-[#5C5855] mt-2">Styles: Rasta, XL Rasta Dreadlock Tam, Multi-color Vibin'</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="bg-[#F9F6F0] rounded-lg p-8 border border-[#EADCCC]"
            >
              <h3 className="text-xl font-[Playfair_Display] text-[#2A2626] mb-3">Premium Hats</h3>
              <p className="text-sm text-[#5C5855] leading-relaxed mb-4">
                Higher-tier designs with more intricate stitch patterns, color combinations, and construction time. Each premium hat uses more yarn and takes additional hours to complete.
              </p>
              <p className="text-[#A85A43] font-[Manrope] font-semibold">$45 - $65</p>
              <p className="text-xs text-[#5C5855] mt-2">Styles: Raspberry Hombre ($45), Supreme ($65)</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="bg-[#F9F6F0] rounded-lg p-8 border border-[#EADCCC]"
            >
              <h3 className="text-xl font-[Playfair_Display] text-[#2A2626] mb-3">Scarves & Accessories</h3>
              <p className="text-sm text-[#5C5855] leading-relaxed mb-4">
                Crocheted infinity scarves and sun hats. The Blue Infinity Scarf is a continuous-loop design that can be wrapped once or twice for different looks. Summer Sun is a wide-brimmed crochet sun hat.
              </p>
              <p className="text-[#A85A43] font-[Manrope] font-semibold">$30 - $50</p>
              <p className="text-xs text-[#5C5855] mt-2">Styles: Blue Infinity Scarf ($50), Summer Sun ($30)</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
              className="bg-[#F9F6F0] rounded-lg p-8 border border-[#EADCCC]"
            >
              <h3 className="text-xl font-[Playfair_Display] text-[#2A2626] mb-3">Signature Shawls</h3>
              <p className="text-sm text-[#5C5855] leading-relaxed mb-4">
                The Aurora Chevron Shawl is our flagship product: a 60" x 30" multicolor chevron-pattern shawl with 15+ yarn colors. It takes over 40 hours to complete using a ripple crochet stitch technique. Wearable as a wrap, poncho, or drape.
              </p>
              <p className="text-[#A85A43] font-[Manrope] font-semibold">$100 (Free Shipping)</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6 }}
              className="bg-[#F9F6F0] rounded-lg p-8 border border-[#EADCCC]"
            >
              <h3 className="text-xl font-[Playfair_Display] text-[#2A2626] mb-3">Custom Orders</h3>
              <p className="text-sm text-[#5C5855] leading-relaxed mb-4">
                Have a specific design in mind? We accept custom orders where you choose the style, colors, and size. Custom beanies start at $30. Typical turnaround is 5-10 business days depending on complexity. Submit requests on our Products page.
              </p>
              <p className="text-[#A85A43] font-[Manrope] font-semibold">Starting at $30</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Materials & Craftsmanship */}
      <section className="py-24" data-testid="materials-section">
        <div className="max-w-6xl mx-auto px-6">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl sm:text-5xl font-[Playfair_Display] text-[#2A2626] tracking-tight text-center mb-16"
          >
            Materials & Craftsmanship
          </motion.h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-[Playfair_Display] text-[#2A2626] mb-4">Yarn & Materials</h3>
              <div className="space-y-4 text-[#5C5855] text-sm leading-relaxed">
                <p>
                  We primarily use <strong>soft acrylic yarn</strong> for its ideal combination of softness, durability, and easy care. Acrylic yarn is hypoallergenic (safe for sensitive skin), machine washable, and holds its color and shape through many wears and washes.
                </p>
                <p>
                  For warmer-weather designs, we use <strong>cotton-blend yarns</strong> that offer better breathability while maintaining structure. Our summer hats and lighter-weight beanies typically use these blends.
                </p>
                <p>
                  All yarns are sourced from trusted U.S. suppliers and selected for colorfastness, softness against hair and skin, and consistent weight. We use medium-weight (worsted, #4) yarn for most hats and bulky-weight (#5) for oversized designs like the XL Rasta Dreadlock Tam.
                </p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-[Playfair_Display] text-[#2A2626] mb-4">Crochet Techniques</h3>
              <div className="space-y-4 text-[#5C5855] text-sm leading-relaxed">
                <p>
                  Christian uses several crochet stitch techniques depending on the design:
                </p>
                <ul className="space-y-2 ml-4">
                  <li><strong>Single Crochet (SC):</strong> Creates a tight, dense fabric. Used for hat brims and structured sections.</li>
                  <li><strong>Half-Double Crochet (HDC):</strong> A slightly taller stitch that balances warmth with flexibility. Used in most beanie bodies.</li>
                  <li><strong>Double Crochet (DC):</strong> Creates a more open, breathable fabric. Used in summer hats and decorative sections.</li>
                  <li><strong>Ripple/Chevron Stitch:</strong> Creates the signature zigzag pattern of the Aurora Chevron Shawl. Requires precise stitch counting and color changes.</li>
                </ul>
                <p>
                  Crochet hooks range from size H/8 (5.0mm) for standard hats to K/10.5 (6.5mm) for oversized items. All crocheting is done by hand -- no looms, no knitting machines.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Values Section */}
      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl sm:text-5xl font-[Playfair_Display] text-[#2A2626] tracking-tight text-center mb-16"
          >
            What We Stand For
          </motion.h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-center"
            >
              <div className="w-16 h-16 bg-[#A85A43]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Heart className="w-8 h-8 text-[#A85A43]" />
              </div>
              <h3 className="text-xl font-[Playfair_Display] text-[#2A2626] mb-3">Handcrafted with Love</h3>
              <p className="text-[#5C5855] leading-relaxed text-sm">
                Every stitch is made with care by Christian Clark. We don't outsource, mass produce, or cut corners. Each item is a labor of love that takes hours of focused hand crochet work.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-center"
            >
              <div className="w-16 h-16 bg-[#A85A43]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Sparkles className="w-8 h-8 text-[#A85A43]" />
              </div>
              <h3 className="text-xl font-[Playfair_Display] text-[#2A2626] mb-3">One-of-a-Kind</h3>
              <p className="text-[#5C5855] leading-relaxed text-sm">
                No two pieces are exactly alike. Even within the same design, slight variations in tension, color placement, and finishing make every hat unique. That's the beauty of handmade.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="text-center"
            >
              <div className="w-16 h-16 bg-[#A85A43]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Award className="w-8 h-8 text-[#A85A43]" />
              </div>
              <h3 className="text-xl font-[Playfair_Display] text-[#2A2626] mb-3">Inclusive Design</h3>
              <p className="text-[#5C5855] leading-relaxed text-sm">
                Our hats are designed to work for everyone, especially people with dreadlocks, braids, twists, and natural hair who struggle to find comfortable, well-fitting hats in stores.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Shipping & Care */}
      <section className="py-24 bg-[#F9F6F0]" data-testid="shipping-care-section">
        <div className="max-w-6xl mx-auto px-6">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl font-[Playfair_Display] text-[#2A2626] tracking-tight text-center mb-16"
          >
            Shipping & Care Guide
          </motion.h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-lg p-8 border border-[#EADCCC]"
            >
              <h3 className="text-xl font-[Playfair_Display] text-[#2A2626] mb-4 flex items-center gap-2">
                <ShoppingBag className="w-5 h-5 text-[#A85A43]" />
                Shipping Information
              </h3>
              <ul className="space-y-3 text-[#5C5855] text-sm leading-relaxed">
                <li><strong>Processing time:</strong> In-stock items ship within 3-5 business days. Custom orders ship within 7-10 business days.</li>
                <li><strong>Carrier:</strong> USPS from Sacramento, CA.</li>
                <li><strong>Nationwide delivery:</strong> We ship to all 50 U.S. states.</li>
                <li><strong>Free shipping:</strong> The Aurora Chevron Shawl ($100) includes free shipping.</li>
                <li><strong>Tracking:</strong> A tracking number is provided with every order.</li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-lg p-8 border border-[#EADCCC]"
            >
              <h3 className="text-xl font-[Playfair_Display] text-[#2A2626] mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#A85A43]" />
                Care Instructions
              </h3>
              <ul className="space-y-3 text-[#5C5855] text-sm leading-relaxed">
                <li><strong>Washing:</strong> Machine wash on gentle/delicate cycle with cold water, or hand wash with mild detergent.</li>
                <li><strong>Drying:</strong> Lay flat on a clean towel to air dry. Do not wring, twist, or tumble dry on high heat.</li>
                <li><strong>Storage:</strong> Store flat or loosely rolled in a drawer or shelf. Avoid hanging on hooks, which can stretch the fabric.</li>
                <li><strong>Pilling:</strong> Minor pilling may occur with regular wear. Use a fabric shaver or pill comb to remove.</li>
                <li><strong>Longevity:</strong> With proper care, our crocheted items last for years.</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-[Playfair_Display] text-[#2A2626] tracking-tight mb-6">
              Ready to Protect Your Crown?
            </h2>
            <p className="text-lg text-[#5C5855] leading-relaxed mb-8">
              Browse our full collection of 15 handcrafted crocheted hats, scarves, and shawls. Prices start at $30, with custom orders available. Based in Sacramento, shipping nationwide.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/products"
                className="inline-block bg-[#A85A43] hover:bg-[#8C4935] text-white font-[Manrope] py-4 px-8 rounded-full transition-colors"
                data-testid="about-shop-now-button"
              >
                Shop All Products
              </Link>
              <Link
                to="/contact"
                className="inline-block bg-white hover:bg-[#F9F6F0] text-[#2A2626] font-[Manrope] py-4 px-8 rounded-full border border-[#EADCCC] transition-colors"
                data-testid="about-contact-button"
              >
                Contact Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
